import { useState, useMemo, useEffect, useRef, KeyboardEvent } from 'react';
import type { ChangeEvent } from 'react';
import type { IconPackId } from 'dragonmouse-shared/interfaces';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { ICON_NAMES } from 'designSystem/iconNames';
import { DEFAULT_ICON_PACK, ICON_PACKS } from 'dragonmouse-shared/constants';
import * as S from './styles';

interface IconNameInputProps {
	iconPack: IconPackId;
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
}

const IconNameInput = ({
	iconPack,
	value,
	onChange,
	placeholder = 'Search icons...',
}: IconNameInputProps) => {
	const [showDropdown, setShowDropdown] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

	const iconNames = ICON_NAMES[iconPack] ?? ICON_NAMES[DEFAULT_ICON_PACK];
	const searchUrl = ICON_PACKS[iconPack]?.searchUrl;

	// Deduplicate and filter in one pass
	const filteredIcons = useMemo(() => {
		const seen = new Set<string>();
		const result: string[] = [];
		const query = value.toLowerCase();
		for (const name of iconNames) {
			if (seen.has(name)) continue;
			seen.add(name);
			if (!query || name.toLowerCase().includes(query)) {
				result.push(name);
			}
		}
		return result;
	}, [value, iconNames]);

	useEffect(() => {
		setActiveIndex(-1);
		itemRefs.current.length = filteredIcons.length;
	}, [filteredIcons]);

	useEffect(() => {
		if (activeIndex >= 0 && itemRefs.current[activeIndex]) {
			itemRefs.current[activeIndex]?.scrollIntoView({ block: 'nearest' });
		}
	}, [activeIndex]);

	useOnClickOutside(dropdownRef, () => {
		if (showDropdown) {
			setShowDropdown(false);
		}
	});

	const onSelect = (name: string) => {
		onChange(name);
		setShowDropdown(false);
		setActiveIndex(-1);
	};

	const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		onChange(e.target.value);
		setShowDropdown(true);
	};

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (!showDropdown && e.key !== 'Escape') {
			if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
				setShowDropdown(true);
			}
			return;
		}

		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault();
				setActiveIndex((prev) => Math.min(prev + 1, filteredIcons.length - 1));
				break;
			case 'ArrowUp':
				e.preventDefault();
				setActiveIndex((prev) => Math.max(prev - 1, 0));
				break;
			case 'Enter':
				e.preventDefault();
				if (activeIndex >= 0 && filteredIcons[activeIndex]) {
					onSelect(filteredIcons[activeIndex]);
				}
				break;
			case 'Escape':
				e.preventDefault();
				setShowDropdown(false);
				setActiveIndex(-1);
				break;
		}
	};

	return (
		<S.Wrapper ref={dropdownRef}>
			<S.Input
				type="text"
				value={value}
				placeholder={placeholder}
				onChange={onInputChange}
				onKeyDown={onKeyDown}
				onFocus={() => setShowDropdown(true)}
				onContextMenu={() => setShowDropdown(false)}
			/>
			{showDropdown && (filteredIcons.length > 0 || searchUrl) && (
				<S.Dropdown>
					<S.DropdownList>
						{filteredIcons.map((name, index) => (
							<S.DropdownItem
								key={`${name}-${index}`}
								$active={index === activeIndex}
								ref={(el) => {
									itemRefs.current[index] = el;
								}}
								onClick={() => onSelect(name)}
							>
								{name}
							</S.DropdownItem>
						))}
					</S.DropdownList>
					{searchUrl && (
						<S.BrowseLink href={searchUrl} target="_blank" rel="noopener noreferrer">
							Browse all icons
						</S.BrowseLink>
					)}
				</S.Dropdown>
			)}
		</S.Wrapper>
	);
};

export default IconNameInput;
